import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiPayloadCreator } from "util/api";
import { selectAuthToken } from "redux/features/auth";

import { sortOn, caseInsensitive } from "util/sort";

const apiCall = createAsyncThunk(
  "profile/apiCall",
  apiPayloadCreator(["api", "/people"], "get")
);

function canonicalizeProfilePayload(payload) {
  const canon = { ...payload };
  if (canon.email_addresses) {
    canon.email_addresses = canon.email_addresses.sort(
      sortOn("address", caseInsensitive)
    );
  }
  return canon;
}

// The profile data structure contains not only items that are manipulated as
// a whole, such as a person's name or list of affiliations, but also items
// that are collections of subresources each capable of individual editing,
// such as a person's participation in a particular activity.

// The "subresources" object maps special key strings to callbacks capable
// of taking the profile data and an updated subresource object and replacing
// that subresource at the appropriate place in the appropriate collection in
// the data.

const subresources = {
  participation: (data, newSummary) => {
    // Don't do anything if this is a stub resource representing future
    // potential participation in an activity but with no actual participation
    // record created yet.
    if (newSummary.id === undefined) return;

    data.participations = data.participations.map((p) =>
      p.id === newSummary.id ? newSummary : p
    );
    if (data.participations.filter((p) => p.id === newSummary.id).length === 0)
      data.participations.push(newSummary);
  },
};

// Regular values in the profile are updated simply by replacing the value
// associated to the specified key.
const regularUpdate = (key) => (data, value) => {
  data[key] = value;
};

export const profileSlice = createSlice({
  name: "profile",
  initialState: {
    id: null,
    pending: false,
    error: null,
    registrationPending: null,
    registrationProgress: null,
    itemStatus: {
      affiliation: { reviewed: false, incomplete: null },
      demographics: {
        reviewed: false,
        incomplete: null,
      },
    },
  },
  reducers: {
    updatePerson: (state, action) => {
      const payload = canonicalizeProfilePayload(action.payload);
      const { public_id } = payload;
      if (public_id !== state.id) {
        console.error("Profile store is wrong id");
      } else {
        Object.entries(payload).forEach(([field, value]) => {
          const updater = subresources[field] || regularUpdate(field);
          updater(state.data, value);
        });
      }
    },
    setAffiliationIncomplete: (state, action) => {
      state.itemStatus.affiliation.incomplete = action.payload;
    },
    setDemographicItemsIncomplete: (state, action) => {
      state.itemStatus.demographics.incomplete =
        action.payload === null
          ? null
          : {
              ...(state.itemStatus.demographics.incomplete || {}),
              ...action.payload,
            };
    },

    setRegistrationPending: (state, action) => {
      state.registrationPending = action.payload;
      if (action.payload === null) state.registrationProgress = null;
    },

    setRegistrationProgress: (state, action) => {
      state.registrationProgress = action.payload;
    },

    setAffiliationReviewed: (state, action) => {
      state.itemStatus.affiliation.reviewed = action.payload;
    },

    setDemographicsReviewed: (state, action) => {
      state.itemStatus.demographics.reviewed = action.payload;
    },
  },
  extraReducers: {
    [apiCall.pending]: (state, action) => {
      const { arg } = action.meta;
      const { id } = arg;
      state.id = id;
      state.pending = true;
      state.error = null;
    },
    [apiCall.fulfilled]: (state, action) => {
      const { arg } = action.meta;
      const { id } = arg;
      state.id = id;
      state.pending = false;
      state.error = null;
      state.data = canonicalizeProfilePayload(action.payload);
    },
    [apiCall.rejected]: (state, action) => {
      const { arg } = action.meta;
      const { id } = arg;
      state.id = id;
      state.pending = false;
      state.failed = true;
      state.error = action.payload || { message: "Unknown error" };
    },
  },
});

profileSlice.selector = (store) => store.profile;

profileSlice.asyncActions = {
  load: (id) => (dispatch, getState) =>
    dispatch(
      apiCall({
        id: id,
        path: "/" + id,
        params: { full_profile: 1 },
        selectAuthToken,
      })
    ),
};

export default profileSlice.reducer;
